const eof = 'eof';

enum TokenType {
    UNKNOWN,
    NUMBER,
    RELATION,
    VARIABLE,
    FUNC,
    OPERATOR,
    BRACKET,
    SPACE,
}

interface IToken {
    type: TokenType;
    value: string;
}

type state = ((l: Scanner) => state) | null;

const digits = '0123456789';
const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
const operators = `+-*/^:,`;
const brackets = `(){}[]`;

/**
 * Scanner is a LaTeX equation scanner that performs simple tokenisation of an input
 * LaTeX equation. The tokens will be returned from the `run` method.
 */
class Scanner {
    static startState: state = Scanner.rootState;

    // List of items that are parsed.
    private readonly items: IToken[];
    // The input string to parse.
    private readonly input: string;

    private start = 0;
    private pos = 0;
    private width = 0;

    constructor(input: string) {
        this.input = input || '';
        this.items = [];
    }

    /**
     * Emit a token using the current position and length.
     * @param type
     */
    private emit(type: TokenType) {
        const value = this.input.substr(this.start, this.pos - this.start);
        this.items.push({ type, value });
        this.start = this.pos;
    }

    /**
     * Step to the next character in the input.
     */
    private next(): string {
        if (this.pos >= this.input.length) {
            this.width = 0;
            return eof;
        }
        this.width = 1;
        return this.input[this.pos++];
    }

    /**
     * Eat the next character if it is in the set of valid characters. Returns if the next
     * character was eaten.
     * @param valid - string containing valid characters
     */
    private accept(valid: string): boolean {
        if (valid.includes(this.next())) {
            return true;
        }
        this.backup();
        return false;
    }

    /**
     * Accept the next characters if they are in the set of valid characters.
     * @param valid - string containing valid characters
     */
    private acceptRun(valid: string) {
        while (valid.includes(this.next())) {
            /* ignore */
        }
        this.backup();
    }

    /**
     * Reverse the position of the scanner.
     */
    private backup() {
        this.pos -= this.width;
    }

    /**
     * Run the scanner and return the array of tokens that are parsed from the input string.
     */
    public run(): IToken[] {
        for (let state = Scanner.startState; state !== null; state) {
            state = state(this);
        }
        return this.items;
    }

    static rootState(l: Scanner): state {
        const r = l.next();
        switch (true) {
            case r === eof:
                return null;
            case digits.includes(r):
                l.backup();
                return Scanner.numberState;
            case r === '\\':
                return Scanner.functionState;
            case chars.includes(r):
                l.emit(TokenType.VARIABLE);
                break;
            case operators.includes(r):
                l.emit(TokenType.OPERATOR);
                break;
            case brackets.includes(r):
                l.emit(TokenType.BRACKET);
                break;
            case r === ' ':
                l.acceptRun(' ');
                l.emit(TokenType.SPACE);
                break;
            case '<>='.includes(r):
                if (r === '<' || r === '>') {
                    l.accept('=');
                }
                l.emit(TokenType.RELATION);
                break;
            default:
                throw `unknown rune: ${r}`;
        }
        return Scanner.rootState;
    }

    static numberState(l: Scanner): state {
        l.accept('+-');
        l.acceptRun(digits);
        if (l.accept('.')) {
            l.acceptRun(digits);
        }
        l.emit(TokenType.NUMBER);
        return Scanner.rootState;
    }

    static functionState(l: Scanner): state {
        l.accept('\\');
        l.acceptRun(chars);
        l.emit(TokenType.FUNC);
        return Scanner.rootState;
    }
}

const functionMap = {
    '\\left': '', // ignored
    '\\right': '', // ignored
    '\\times': 'xx',
    '\\div': '-:',
};

const bracketMapping = {
    '(': '(',
    ')': ')',
    '{': '(',
    '}': ')',
};

const asciiReplacements = {
    [TokenType.FUNC]: (val: string): string =>
        functionMap[val] !== undefined ? functionMap[val] : val.replace(/^\\/, ''),
    [TokenType.BRACKET]: (val: string): string => bracketMapping[val],
};

/**
 * Convert a LaTeX equation into asciimath. The output parts will be very separated by spaces
 * to ensure that there is no conflict in variable names an functions.
 * @param input - latex equation string
 */
function latexToAsciimath(input: string): string {
    const l = new Scanner(input);
    const items = l.run();

    let output = '';
    for (const token of items) {
        const value = asciiReplacements[token.type]
            ? asciiReplacements[token.type](token.value)
            : token.value;
        if (value !== '') {
            output += value + ' ';
        }
    }
    // Trim outer space and remove double spaces
    return output.replace(/\s+/g, ' ').trim();
}

export { latexToAsciimath };
